import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useForm } from "@mantine/form";
import {
     TextInput,
     PasswordInput,
     Button,
     Stack,
     Checkbox,
     Text,
     Anchor,
     Alert,
     PinInput,
     Center,
     Group,
} from "@mantine/core";
import { STORAGE_TOKEN_KEY, useAuth } from "../../contexts/AuthContext";
import { useToastNotification } from "../../hooks/useToastNotification";
import {
     IconLock,
     IconPhone,
} from "@tabler/icons-react";
import { HttpCodes, Logo, AppName } from "../../lib/data";
import "./styles.scss";
import { Endpoints } from "../../lib/endpoints";
import { PerformRequest } from "../../lib/usePerformRequest";
import { NonPaginatedResponse } from "../../types";
import { IUserProps } from "../../types/User";

export default function Login() {
     const navigate = useNavigate();
     const [isLoading, setLoading] = useState(false);
     const [error, setError] = useState<string | null>(null);
     const { reloadUser } = useAuth();
     const { success, error: showError } = useToastNotification();
     const [otpSent, setOtpSent] = useState(false);
     const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
     const [phoneForOtp, setPhoneForOtp] = useState<string>("");
     const [passwordForTwoFactor, setPasswordForTwoFactor] =
          useState<string>("");
     const [phoneForDisplay, setPhoneForDisplay] = useState<string>("");
     const [cooldownTimer, setCooldownTimer] = useState(0);
     const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
     const [isVerifyingTwoFactor, setIsVerifyingTwoFactor] = useState(false);
     const [isResendingOtp, setIsResendingOtp] = useState(false);

     const loginForm = useForm({
          initialValues: {
               phoneNumber: "",
               password: "",
               rememberMe: false,
          },
          validate: {
               phoneNumber: (value: string) => {
                    if (!value) return "Phone number is required";
                    if (!/^\d+$/.test(value)) return "Only digits are allowed";
                    return value.length >= 10
                         ? null
                         : "Enter at least 10 digits";
               },
               password: (value: string) => {
                    if (!value) return "Password is required";
                    if (value.length < 8)
                         return "Password must be at least 8 characters";
                    if (!/[a-z]/.test(value))
                         return "Password must contain at least one lowercase letter";
                    if (!/[A-Z]/.test(value))
                         return "Password must contain at least one uppercase letter";
                    if (!/[0-9]/.test(value))
                         return "Password must contain at least one number";
                    return null;
               },
          },
     });

     const otpForm = useForm({
          initialValues: {
               otp: "",
          },
          validate: {
               otp: (value: string) => {
                    if (!value) return "OTP is required";
                    if (value.length !== 6) return "OTP must be 6 digits";
                    return null;
               },
          },
     });


     // Cooldown timer effect
     useEffect(() => {
          if (cooldownTimer > 0) {
               const timer = setInterval(() => {
                    setCooldownTimer((prev) => Math.max(0, prev - 1));
               }, 1000);
               return () => clearInterval(timer);
          }
     }, [cooldownTimer]);

     const HandleLoginUser = async () => {
          setError(null);
          const { hasErrors } = loginForm.validate();
          if (!hasErrors) {
               setLoading(true);
               try {
                    const { phoneNumber, password } = loginForm.values;
                    const r = await PerformRequest<
                         NonPaginatedResponse<{
                              token: string;
                              user: IUserProps;
                         }>
                    >({
                         method: Endpoints.GetLogin().method,
                         route: Endpoints.GetLogin().route,
                         data: {
                              emailOrPhone: phoneNumber,
                              password,
                         },
                         doNotUseAuthorization: true,
                    });
                    if (r) {
                         if (r.status === HttpCodes.Success) {
                              // Check if 2FA is required
                              if ((r?.data?.data as any)?.requiresTwoFactor) {
                                   setRequiresTwoFactor(true);
                                   setPhoneForOtp(phoneNumber);
                                   setPasswordForTwoFactor(password);
                                   setPhoneForDisplay(phoneNumber);
                                   success("Please enter your 2FA code");
                              } else {
                                   // Login successful - token returned directly
                                   const { token } = r?.data?.data || {};
                                   if (token) {
                                        // Set token in localStorage FIRST
                                        localStorage.setItem(
                                             STORAGE_TOKEN_KEY,
                                             token
                                        );
                                        // Reload user profile after saving token
                                        // Wait for profile to load before navigating
                                        try {
                                             await reloadUser();
                                             // Double check token is still there (in case reloadUser cleared it)
                                             const tokenStillExists = !!localStorage.getItem(STORAGE_TOKEN_KEY);
                                             if (tokenStillExists) {
                                                  success("Login successful!");
                                                  // Small delay to ensure state is updated
                                                  setTimeout(() => {
                                                       navigate("/dashboard/request-loan");
                                                  }, 100);
                                             } else {
                                                  const errorMessage = "Failed to load user profile";
                                                  setError(errorMessage);
                                                  showError(errorMessage);
                                             }
                                        } catch (err: any) {
                                             const errorMessage = err.message || "Failed to load user profile";
                                             setError(errorMessage);
                                             showError(errorMessage);
                                             // Clear token if profile load failed
                                             localStorage.removeItem(STORAGE_TOKEN_KEY);
                                        }
                                   } else {
                                        const errorMessage =
                                             "Invalid response from server";
                                        setError(errorMessage);
                                        showError(errorMessage);
                                   }
                              }
                         } else {
                              const errorMessage = r.message || "Login failed";
                              setError(errorMessage);
                              showError(errorMessage);
                         }
                    }
               } catch (err: any) {
                    const errorMessage = err.message || "Login failed";
                    setError(errorMessage);
                    showError(errorMessage);
               } finally {
                    setLoading(false);
               }
          }
     };

     const HandleVerifyTwoFactor = async () => {
          setError(null);
          const { hasErrors } = otpForm.validate();
          if (!hasErrors) {
               setIsVerifyingTwoFactor(true);
               try {
                    const { otp } = otpForm.values;
                    const r = await PerformRequest<
                         NonPaginatedResponse<{
                              token: string;
                              user: IUserProps;
                         }>
                    >({
                         method: Endpoints.GetVerifyTwoFactorLogin().method,
                         route: Endpoints.GetVerifyTwoFactorLogin().route,
                         data: {
                              emailOrPhone: phoneForOtp,
                              password: passwordForTwoFactor,
                              twoFactorCode: otp,
                         },
                         doNotUseAuthorization: true,
                    });
                    if (r) {
                         if (r.status === HttpCodes.Success) {
                              const { token } = r?.data?.data;
                              // Set token in localStorage FIRST
                              localStorage.setItem(STORAGE_TOKEN_KEY, token);
                              // Reload user profile after saving token
                              // Wait for profile to load before navigating
                              try {
                                   await reloadUser();
                                   // Double check token is still there (in case reloadUser cleared it)
                                   const tokenStillExists = !!localStorage.getItem(STORAGE_TOKEN_KEY);
                                   if (tokenStillExists) {
                                        success("Login successful!");
                                        // Small delay to ensure state is updated
                                        setTimeout(() => {
                                             navigate("/dashboard/request-loan");
                                        }, 100);
                                   } else {
                                        const errorMessage = "Failed to load user profile";
                                        setError(errorMessage);
                                        showError(errorMessage);
                                   }
                              } catch (err: any) {
                                   const errorMessage = err.message || "Failed to load user profile";
                                   setError(errorMessage);
                                   showError(errorMessage);
                                   // Clear token if profile load failed
                                   localStorage.removeItem(STORAGE_TOKEN_KEY);
                              }
                         } else {
                              const errorMessage =
                                   r.message || "Invalid 2FA code";
                              setError(errorMessage);
                              showError(errorMessage);
                         }
                    }
               } catch (err: any) {
                    const errorMessage = err.message || "Verification failed";
                    setError(errorMessage);
                    showError(errorMessage);
               } finally {
                    setIsVerifyingTwoFactor(false);
               }
          }
     };

     const HandleVerifyOtp = async () => {
          setError(null);
          const { hasErrors } = otpForm.validate();
          if (!hasErrors) {
               setIsVerifyingOtp(true);
               try {
                    const { otp } = otpForm.values;
                    const r = await PerformRequest<
                         NonPaginatedResponse<{
                              token: string;
                              user: IUserProps;
                         }>
                    >({
                         method: Endpoints.GetVerifyLoginOtp().method,
                         route: Endpoints.GetVerifyLoginOtp().route,
                         data: {
                              emailOrPhone: phoneForOtp,
                              otp,
                         },
                         doNotUseAuthorization: true,
                    });
                    if (r) {
                         if (r.status === HttpCodes.Success) {
                              const { token } = r?.data?.data;
                              // Set token in localStorage FIRST
                              localStorage.setItem(STORAGE_TOKEN_KEY, token);
                              // Reload user profile after saving token
                              // Wait for profile to load before navigating
                              try {
                                   await reloadUser();
                                   // Double check token is still there (in case reloadUser cleared it)
                                   const tokenStillExists = !!localStorage.getItem(STORAGE_TOKEN_KEY);
                                   if (tokenStillExists) {
                                        success("Login successful!");
                                        // Small delay to ensure state is updated
                                        setTimeout(() => {
                                             navigate("/dashboard/request-loan");
                                        }, 100);
                                   } else {
                                        const errorMessage = "Failed to load user profile";
                                        setError(errorMessage);
                                        showError(errorMessage);
                                   }
                              } catch (err: any) {
                                   const errorMessage = err.message || "Failed to load user profile";
                                   setError(errorMessage);
                                   showError(errorMessage);
                                   // Clear token if profile load failed
                                   localStorage.removeItem(STORAGE_TOKEN_KEY);
                              }
                         } else {
                              const errorMessage = r.message || "Invalid OTP";
                              setError(errorMessage);
                              showError(errorMessage);
                         }
                    }
               } catch (err: any) {
                    const errorMessage = err.message || "Verification failed";
                    setError(errorMessage);
                    showError(errorMessage);
               } finally {
                    setIsVerifyingOtp(false);
               }
          }
     };

     const HandleResendOtp = async () => {
          if (cooldownTimer > 0) return;

          setError(null);
          setIsResendingOtp(true);
          try {
               const r = await PerformRequest<
                    NonPaginatedResponse<{
                         message?: string;
                         email?: string;
                         otp?: string;
                    }>
               >({
                    method: Endpoints.GetResendLoginOtp().method,
                    route: Endpoints.GetResendLoginOtp().route,
                    data: {
                         emailOrPhone: phoneForOtp,
                    },
                    doNotUseAuthorization: true,
               });
               if (r) {
                    if (r.status === HttpCodes.Success) {
                         setCooldownTimer(30); // Reset 30-second cooldown
                         otpForm.setFieldValue("otp", "");
                         success(
                              r?.data?.data?.message ||
                                   "OTP resent to your phone"
                         );
                    } else {
                         const errorMessage =
                              r.message || "Failed to resend OTP";
                         setError(errorMessage);
                         showError(errorMessage);
                    }
               }
          } catch (err: any) {
               const errorMessage = err.message || "Failed to resend OTP";
               setError(errorMessage);
               showError(errorMessage);
          } finally {
               setIsResendingOtp(false);
          }
     };

     return (
          <div className="min-h-screen flex">
               {/* Login Form */}
               <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="w-full flex items-center justify-center p-8 bg-white login-container"
               >
                    <div className="w-full max-w-md">
                         {/* Logo */}
                         <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{
                                   delay: 0.2,
                                   type: "spring",
                                   stiffness: 200,
                              }}
                              className="flex justify-center mb-8"
                         >
                              <img
                                   src={Logo}
                                   alt={`${AppName} Logo`}
                                   className="h-16"
                              />
                         </motion.div>

                         {/* Header */}
                         <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.3 }}
                              className="text-center mb-10"
                         >
                              <h1 className="text-4xl font-bold text-gray-900 mb-3">
                                   {otpSent || requiresTwoFactor
                                        ? requiresTwoFactor
                                             ? "Enter 2FA Code"
                                             : "Verify Your Code"
                                        : "Welcome Back!"}
                              </h1>
                              <p className="text-gray-500 text-base">
                                   {otpSent || requiresTwoFactor
                                        ? requiresTwoFactor
                                             ? "Enter the code from your authenticator app"
                                             : "Enter the 6-digit code sent to your phone"
                                        : "Access your account to manage loans and financial services with ease"}
                              </p>
                         </motion.div>

                         {/* Error Alert */}
                         {error && (
                              <motion.div
                                   initial={{ opacity: 0, height: 0 }}
                                   animate={{ opacity: 1, height: "auto" }}
                                   className="mb-6"
                              >
                                   <Alert color="red" title="Error">
                                        {error}
                                   </Alert>
                              </motion.div>
                         )}

                         {/* 2FA or OTP Verification Step */}
                         {otpSent || requiresTwoFactor ? (
                              <motion.div
                                   initial={{ opacity: 0, y: 20 }}
                                   animate={{ opacity: 1, y: 0 }}
                              >
                                   <form
                                        onSubmit={(e) => {
                                             e.preventDefault();
                                             if (requiresTwoFactor) {
                                                  HandleVerifyTwoFactor();
                                             } else {
                                                  HandleVerifyOtp();
                                             }
                                        }}
                                   >
                                        <Stack gap="md">
                                             <div className="text-center mb-4">
                                                  <Text
                                                       size="lg"
                                                       fw={600}
                                                       c="dimmed"
                                                  >
                                                       {requiresTwoFactor
                                                            ? "Enter 2FA Code"
                                                            : "Enter Verification Code"}
                                                  </Text>
                                                  <Text
                                                       size="sm"
                                                       c="dimmed"
                                                       mt="xs"
                                                  >
                                                       {requiresTwoFactor ? (
                                                            <>
                                                                 Enter the
                                                                 6-digit code
                                                                 from your
                                                                 authenticator
                                                                 app
                                                                 <br />
                                                                 <strong>
                                                                      {
                                                                           phoneForDisplay
                                                                      }
                                                                 </strong>
                                                            </>
                                                       ) : (
                                                            <>
                                                                 We've sent a
                                                                 6-digit code to{" "}
                                                                 <strong>
                                                                      {
                                                                           phoneForDisplay
                                                                      }
                                                                 </strong>
                                                            </>
                                                       )}
                                                  </Text>
                                             </div>

                                             <Center>
                                                  <PinInput
                                                       length={6}
                                                       size="md"
                                                       {...otpForm.getInputProps(
                                                            "otp"
                                                       )}
                                                       type="number"
                                                       oneTimeCode
                                                       autoFocus
                                                  />
                                             </Center>

                                             {otpForm.errors.otp && (
                                                  <Text
                                                       size="sm"
                                                       c="red"
                                                       ta="center"
                                                  >
                                                       {otpForm.errors.otp}
                                                  </Text>
                                             )}

                                             <Group
                                                  justify="space-between"
                                                  mt="md"
                                             >
                                                  <Button
                                                       variant="subtle"
                                                       onClick={() => {
                                                            setOtpSent(false);
                                                            setRequiresTwoFactor(
                                                                 false
                                                            );
                                                            otpForm.setFieldValue(
                                                                 "otp",
                                                                 ""
                                                            );
                                                            setError(null);
                                                       }}
                                                       disabled={
                                                            isVerifyingOtp ||
                                                            isVerifyingTwoFactor
                                                       }
                                                  >
                                                       Back
                                                  </Button>
                                                  {!requiresTwoFactor && (
                                                       <Button
                                                            onClick={
                                                                 HandleResendOtp
                                                            }
                                                            variant="subtle"
                                                            disabled={
                                                                 cooldownTimer >
                                                                      0 ||
                                                                 isResendingOtp
                                                            }
                                                            loading={
                                                                 isResendingOtp
                                                            }
                                                            style={{
                                                                 color: "#699269",
                                                            }}
                                                       >
                                                            {cooldownTimer > 0
                                                                 ? `Resend in ${cooldownTimer}s`
                                                                 : "Resend Code"}
                                                       </Button>
                                                  )}
                                             </Group>

                                             <Button
                                                  type="submit"
                                                  fullWidth
                                                  size="md"
                                                  onClick={
                                                       requiresTwoFactor
                                                            ? HandleVerifyTwoFactor
                                                            : HandleVerifyOtp
                                                  }
                                                  loading={
                                                       isVerifyingOtp ||
                                                       isVerifyingTwoFactor
                                                  }
                                                  style={{
                                                       backgroundColor:
                                                            "#699269",
                                                       ":hover": {
                                                            backgroundColor:
                                                                 "#558B2F",
                                                       },
                                                  }}
                                             >
                                                  Verify & Login
                                             </Button>
                                        </Stack>
                                   </form>
                              </motion.div>
                         ) : (
                              /* Login Form */
                              <form
                                   onSubmit={(e) => {
                                        e.preventDefault();
                                        HandleLoginUser();
                                   }}
                              >
                                   <Stack gap="md">
                                        {/* Email or Phone Input */}
                                        <motion.div
                                             initial={{ opacity: 0, y: 20 }}
                                             animate={{ opacity: 1, y: 0 }}
                                             transition={{ delay: 0.4 }}
                                        >
                                             <TextInput
                                                  {...loginForm.getInputProps(
                                                       "phoneNumber"
                                                  )}
                                                  name="phone"
                                                  placeholder="Phone Number"
                                                  leftSection={
                                                       <IconPhone
                                                            size={20}
                                                            stroke={1.5}
                                                       />
                                                  }
                                                  size="md"
                                                  type="tel"
                                                  inputMode="numeric"
                                                  pattern="\d*"
                                                  value={
                                                       loginForm.values
                                                            .phoneNumber
                                                  }
                                                  onChange={(event) => {
                                                       const digitsOnly =
                                                            event.currentTarget.value.replace(
                                                                 /\D/g,
                                                                 ""
                                                            );
                                                       loginForm.setFieldValue(
                                                            "phoneNumber",
                                                            digitsOnly
                                                       );
                                                  }}
                                             />
                                        </motion.div>

                                        {/* Password Input */}
                                        <motion.div
                                             initial={{ opacity: 0, y: 20 }}
                                             animate={{ opacity: 1, y: 0 }}
                                             transition={{ delay: 0.5 }}
                                        >
                                             <PasswordInput
                                                  {...loginForm.getInputProps(
                                                       "password"
                                                  )}
                                                  placeholder="Password"
                                                  leftSection={
                                                       <IconLock
                                                            size={20}
                                                            stroke={1.5}
                                                       />
                                                  }
                                                  size="md"
                                                  onKeyDown={(e) => {
                                                       if (
                                                            e.key ===
                                                            "Enter"
                                                       ) {
                                                            e.preventDefault();
                                                            HandleLoginUser();
                                                       }
                                                  }}
                                             />
                                        </motion.div>

                                        {/* Remember Me & Recovery */}
                                        <motion.div
                                             initial={{ opacity: 0 }}
                                             animate={{ opacity: 1 }}
                                             transition={{ delay: 0.6 }}
                                             className="flex items-center justify-between"
                                        >
                                             <Checkbox
                                                  {...loginForm.getInputProps(
                                                       "rememberMe",
                                                       { type: "checkbox" }
                                                  )}
                                                  label="Remember Me"
                                                  size="sm"
                                             />
                                             <Anchor
                                                  component="button"
                                                  type="button"
                                                  size="sm"
                                                  style={{ color: "#699269" }}
                                             >
                                                  Recovery Password
                                             </Anchor>
                                        </motion.div>

                                        {/* Login Button */}
                                        <motion.div
                                             initial={{ opacity: 0, y: 20 }}
                                             animate={{ opacity: 1, y: 0 }}
                                             transition={{ delay: 0.7 }}
                                        >
                                             <Button
                                                  type="submit"
                                                  fullWidth
                                                  size="md"
                                                  onClick={HandleLoginUser}
                                                  loading={isLoading}
                                                  style={{
                                                       backgroundColor:
                                                            "#699269",
                                                       ":hover": {
                                                            backgroundColor:
                                                                 "#558B2F",
                                                       },
                                                  }}
                                             >
                                                  Login
                                             </Button>
                                        </motion.div>
                                   </Stack>
                              </form>
                         )}

                         {/* Sign Up Link */}
                         <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.9 }}
                              className="mt-8 text-center"
                         >
                              <Text size="sm" c="dimmed">
                                   Don't have an account yet?{" "}
                                   <Anchor
                                        component={Link}
                                        to="/register"
                                        fw={600}
                                        style={{ color: "#699269" }}
                                   >
                                        Sign Up
                                   </Anchor>
                              </Text>
                         </motion.div>
                    </div>
               </motion.div>
          </div>
     );
}
